import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Plus, Edit, Trash2, Search, FileText, ChevronLeft, ChevronRight, 
  User, Home, FolderOpen, Compass, Layers 
} from 'lucide-react';
import * as sketchService from '../services/sketchService';
import { clientService } from '../services/api';
import SketchEditor from './SketchEditor';
import ConfirmModal from './ConfirmModal';
import toast from 'react-hot-toast';

const SketchProjects = () => {
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('ALL'); // 'ALL' | 'SURVEY' | 'STANDALONE'
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(9);
  const [showNewModal, setShowNewModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    client_id: '',
    client_name: '',
    description: '',
    scale: 50
  });
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const res = await clientService.getClients({ limit: 1000 });
      setClients(res.data?.data || res.data || []);
    } catch (err) {
      console.warn('Erro ao carregar clientes:', err);
    }
  };

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      loadProjects();
    }, 300);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await sketchService.getAllProjects({ search });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await sketchService.createProject(formData);
      setShowNewModal(false);
      setFormData({
        name: '',
        client_id: '',
        client_name: '',
        description: '',
        scale: 50
      });
      toast.success('Projeto criado com sucesso');
      loadProjects();
    } catch (error) {
      toast.error('Erro ao criar projeto');
    }
  };

  const handleDelete = async (id) => {
    setConfirmDelete(id);
  };

  const confirmDeleteProject = async () => {
    if (!confirmDelete) return;

    try {
      await sketchService.deleteProject(confirmDelete);
      toast.success('Projeto deletado com sucesso');
      loadProjects();
    } catch (error) {
      toast.error('Erro ao deletar projeto');
    } finally {
      setConfirmDelete(null);
    }
  };

  // Filtros locais e paginação organizada
  const filteredProjects = useMemo(() => {
    let result = [...projects];

    if (filterType === 'SURVEY') {
      result = result.filter(p => p.survey_id || p.environment_id || (p.description && p.description.includes('Levantamento')));
    } else if (filterType === 'STANDALONE') {
      result = result.filter(p => !p.survey_id && !p.environment_id && !(p.description && p.description.includes('Levantamento')));
    }

    return result;
  }, [projects, filterType]);

  const totalPages = Math.max(1, Math.ceil(filteredProjects.length / pageSize));
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredProjects.slice(startIndex, startIndex + pageSize);
  }, [filteredProjects, currentPage, pageSize]);

  // Resetar página quando a busca ou filtro mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterType]);

  const counts = useMemo(() => {
    const total = projects.length;
    const surveyCount = projects.filter(p => p.survey_id || p.environment_id || (p.description && p.description.includes('Levantamento'))).length;
    const standaloneCount = total - surveyCount;
    return { total, surveyCount, standaloneCount };
  }, [projects]);

  if (selectedProject) {
    return (
      <SketchEditor
        projectId={selectedProject}
        onClose={() => {
          setSelectedProject(null);
          loadProjects();
        }}
      />
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-[1780px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-2 rounded-xl bg-blue-50 text-blue-600">
              <Compass className="w-5 h-5" />
            </span>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Croquis Técnicos 2D
            </h1>
          </div>
          <p className="text-sm text-gray-500">
            Desenho arquitetônico, cotas técnicas de marcenaria e pontos prediais de ambientes
          </p>
        </div>

        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2.5 rounded-xl shadow-sm hover:shadow transition-all text-sm shrink-0"
        >
          <Plus className="w-4 h-4" />
          <span>Novo Croqui</span>
        </button>
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Filtros por Categoria de Origem */}
        <div className="flex items-center gap-1.5 p-1 bg-gray-100 rounded-xl overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => setFilterType('ALL')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
              filterType === 'ALL'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>Todos</span>
            <span className="px-1.5 py-0.2 bg-gray-200 text-gray-700 rounded-full text-[10px] font-bold">
              {counts.total}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setFilterType('STANDALONE')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
              filterType === 'STANDALONE'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>Projetos Avulsos</span>
            <span className="px-1.5 py-0.2 bg-blue-100 text-blue-800 rounded-full text-[10px] font-bold">
              {counts.standaloneCount}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setFilterType('SURVEY')}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
              filterType === 'SURVEY'
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <span>Levantamentos de Campo</span>
            <span className="px-1.5 py-0.2 bg-indigo-100 text-indigo-800 rounded-full text-[10px] font-bold">
              {counts.surveyCount}
            </span>
          </button>
        </div>

        {/* Input de Busca */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Buscar por projeto, cliente ou ambiente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs sm:text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white focus:outline-none transition-all"
          />
        </div>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex flex-col justify-center items-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
          <div className="text-gray-500 text-sm font-medium">Carregando croquis...</div>
        </div>
      ) : paginatedProjects.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm px-4">
          <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-3 text-gray-400">
            <FileText className="w-7 h-7" />
          </div>
          <h3 className="text-base font-semibold text-gray-900 mb-1">
            Nenhum croqui encontrado
          </h3>
          <p className="text-xs text-gray-500 max-w-sm mx-auto mb-5">
            {search ? 'Nenhum resultado corresponde à sua pesquisa. Tente buscar com outros termos.' : 'Crie um novo projeto de croqui técnico para iniciar suas pranchas 2D.'}
          </p>
          <button
            onClick={() => setShowNewModal(true)}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-xl text-xs font-semibold hover:bg-blue-700 shadow-sm transition-all"
          >
            <Plus className="w-4 h-4" />
            <span>Criar Novo Croqui</span>
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {paginatedProjects.map((project) => {
            const hasSurvey = Boolean(project.survey_id || (project.description && project.description.includes('Levantamento')));
            const clientName = project.display_client_name || project.client_full_name || project.survey_client_name || project.client_name;
            const environmentName = project.environment_name || (project.description && project.description.match(/ambiente\s+([^-]+)/i)?.[1]?.trim());
            const surveyCode = project.survey_code || (project.description && project.description.match(/Levantamento\s*#?([0-9A-Z-]+)/i)?.[1]?.trim());

            return (
              <div
                key={project.id}
                className="bg-white border border-gray-200/80 hover:border-blue-300 rounded-2xl p-5 hover:shadow-md transition-all flex flex-col justify-between group"
              >
                <div>
                  {/* Cabeçalho do Card */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                        {hasSurvey ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            <Home className="w-3 h-3 text-indigo-500" />
                            {surveyCode ? `Levantamento #${surveyCode}` : 'Levantamento'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                            <Compass className="w-3 h-3 text-blue-500" />
                            Projeto Avulso
                          </span>
                        )}

                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${
                          project.status === 'completed'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : project.status === 'in_progress'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-gray-100 text-gray-700 border-gray-200'
                        }`}>
                          {project.status === 'completed' ? 'Concluído' :
                           project.status === 'in_progress' ? 'Em Andamento' : 'Rascunho'}
                        </span>
                      </div>

                      <h3 className="text-base font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate" title={project.name}>
                        {project.name}
                      </h3>

                      {environmentName && (
                        <p className="text-xs text-indigo-800 font-semibold mt-0.5 flex items-center gap-1 truncate">
                          <span>Ambiente:</span>
                          <span className="text-gray-900 underline decoration-indigo-300">{environmentName}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Vínculo de Cliente */}
                  {clientName && (
                    <div className="mb-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg truncate max-w-full">
                        <User className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">{clientName}</span>
                      </span>
                    </div>
                  )}

                  {project.description && !project.description.startsWith('Croqui do ambiente') && (
                    <p className="text-xs text-gray-500 mb-4 line-clamp-2 leading-relaxed">
                      {project.description}
                    </p>
                  )}
                </div>

                {/* Rodapé e Métricas */}
                <div className="pt-3 border-t border-gray-100 mt-2">
                  <div className="flex items-center justify-between text-[11px] text-gray-500 mb-3">
                    <span className="flex items-center gap-1 font-medium">
                      <Layers className="w-3.5 h-3.5 text-gray-400" />
                      {project.elements_count || 0} {project.elements_count === 1 ? 'elemento' : 'elementos'}
                    </span>
                    <span className="font-mono text-gray-400">
                      Escala 1:{project.scale || 50}
                    </span>
                  </div>

                  <div className="text-[10px] text-gray-400 mb-3 flex items-center justify-between">
                    <span className="truncate">Por {project.created_by_name || 'Equipe'}</span>
                    <span>{new Date(project.created_at).toLocaleDateString('pt-BR')}</span>
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedProject(project.id)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-slate-900 hover:bg-blue-600 text-white font-semibold py-2 px-3 rounded-xl text-xs transition-colors shadow-xs"
                    >
                      <Edit className="w-3.5 h-3.5" />
                      <span>Abrir Prancha</span>
                    </button>
                    
                    <button
                      onClick={() => handleDelete(project.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors border border-transparent hover:border-red-200"
                      title="Excluir Croqui"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginação */}
      {filteredProjects.length > pageSize && (
        <div className="bg-white p-3 sm:p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-600">
          <div>
            Exibindo <span className="font-bold text-gray-900">{((currentPage - 1) * pageSize) + 1}</span> a{' '}
            <span className="font-bold text-gray-900">{Math.min(currentPage * pageSize, filteredProjects.length)}</span> de{' '}
            <span className="font-bold text-gray-900">{filteredProjects.length}</span> croquis
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Página Anterior"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: totalPages }).map((_, idx) => {
              const pNum = idx + 1;
              if (
                pNum === 1 || 
                pNum === totalPages || 
                (pNum >= currentPage - 1 && pNum <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pNum}
                    onClick={() => setCurrentPage(pNum)}
                    className={`w-8 h-8 rounded-lg font-semibold transition-all ${
                      currentPage === pNum
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    {pNum}
                  </button>
                );
              }
              if (pNum === currentPage - 2 || pNum === currentPage + 2) {
                return <span key={pNum} className="px-1 text-gray-400">...</span>;
              }
              return null;
            })}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Próxima Página"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        show={!!confirmDelete}
        title="Deletar Projeto"
        message="Tem certeza que deseja deletar este projeto? Esta ação não pode ser desfeita."
        confirmText="Deletar"
        cancelText="Cancelar"
        type="danger"
        onConfirm={confirmDeleteProject}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* New Project Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                Novo Projeto de Croqui
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Projeto *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Cozinha Planejada Sr. João"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vincular Cliente
                </label>
                <select
                  value={formData.client_id}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    const c = clients.find(cl => String(cl.id) === String(selectedId));
                    setFormData({
                      ...formData,
                      client_id: selectedId,
                      client_name: c ? (c.full_name || c.name) : formData.client_name
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                >
                  <option value="">Selecione um cliente cadastrado (opcional)</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.full_name || c.name} {c.phone ? `(${c.phone})` : ''}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  placeholder="Ou digite o nome do cliente avulso"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Detalhes do projeto..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Escala (1:X)
                </label>
                <input
                  type="number"
                  value={formData.scale}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 10 && value <= 200) {
                      setFormData({ ...formData, scale: value });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min="10"
                  max="200"
                />
              </div>

              <div className="flex items-center space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Criar Projeto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SketchProjects;
